// skeleton/app/src/components/Questions/QuestionsComponent.tsx

import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import httpService from '../../services/httpService';
import {
  ApiEndpoints,
  ImportTemplates,
  DataFieldTypeOptions,
  Constants,
} from '../../constants/appConstants';

import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import SafeHtml from '../Common/SafeHtml';
import DataManagementForm from '../DataManagement/DataManagementForm';
import {
  dataManagementFormConfig,
  getFormValidationErrors,
  normalizeFieldValues,
} from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import { sanitize } from '../../services/renderingService';

import './QuestionsComponent.scss';

const fetchQuestions = () => {
  const url = `${ApiEndpoints.GetAllQuestions}`;
  return httpService.get(url);
};

const fetchTiers = () => {
  const url = `${ApiEndpoints.GetTiersWithInactive}`;
  return httpService.get(url);
};

const fetchMeasures = () => {
  const url = `${ApiEndpoints.GetAllMeasuresWithInactive}`;
  return httpService.get(url);
};

interface QuestionsComponentProps {
  providerTypes: {
    label: string;
    value: string;
  }[];
}

const QuestionsComponent: React.FC<QuestionsComponentProps> = ({
  providerTypes,
}) => {
  useEffect(() => {
    const cleanup = createDialogOverlayStyles(
      'questions-component-styles'
    );

    return cleanup;
  }, []);

  const [questions, setQuestions] = useState<any[]>([]);
  const [tiers, setTiers] = useState<any[]>([]);
  const [measures, setMeasures] = useState<any[]>([]);

  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const [providerTypeFilter, setProviderTypeFilter] =
    useState<string>('');
  const [searchTerm, setSearchTerm] =
    useState<string>('');
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState('');
  const [editDialogVisible, setEditDialogVisible] =
    useState(false);
  const [selectedQuestion, setSelectedQuestion] =
    useState<any>(null);
  const [isAddMode, setIsAddMode] =
    useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState(false);
  const [questionToDelete, setQuestionToDelete] =
    useState<any>(null);

  const handleExcelUpload = async (file: File) => {
    setLoading(true);

    try {
      const fileKey = await uploadFile(
        file,
        Constants.DATA_MANAGEMENT_MODULES.QUESTIONS
      );

      await httpService.post(
        ApiEndpoints.BulkUploadDataManagement,
        {
          fileKey,
          fileName: file.name,
          module:
            Constants.DATA_MANAGEMENT_MODULES.QUESTIONS,
        }
      );

      const response = await fetchQuestions();

      setQuestions(
        Array.isArray(response?.data)
          ? response.data
          : []
      );

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Questions uploaded successfully',
        life: 3000,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Bulk upload failed';

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: msg,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.questions,
    onUpload: handleExcelUpload,
    title: 'Import Questions from Excel',
  });

  useEffect(() => {
    setLoading(true);

    Promise.all([
      fetchQuestions(),
      fetchTiers(),
      fetchMeasures(),
    ])
      .then(
        ([questionsRes, tiersRes, measuresRes]: any[]) => {
          setQuestions(
            Array.isArray(questionsRes?.data)
              ? questionsRes.data
              : []
          );

          setTiers(
            Array.isArray(tiersRes?.data)
              ? tiersRes.data
              : []
          );

          setMeasures(
            Array.isArray(measuresRes?.data)
              ? measuresRes.data
              : []
          );

          setError('');
        }
      )
      .catch((err: any) => {
        setError(
          `Failed to fetch data: ${
            err?.message || 'Unknown error'
          }`
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  /*
   * Provider types used by the page-level filter.
   * Prefer provider types passed from the parent.
   */
  const providerTypeOptions =
    providerTypes && providerTypes.length > 0
      ? providerTypes
      : Array.from(
          new Set(
            questions
              .map((q) => q.providerType)
              .filter(Boolean)
          )
        ).map((providerType) => ({
          label: String(providerType),
          value: String(providerType),
        }));

  /*
   * Filter questions by Provider Type.
   */
  let filteredQuestions = providerTypeFilter
    ? questions.filter(
        (q) =>
          q.providerType === providerTypeFilter
      )
    : questions;

  /*
   * Apply search filter.
   */
  if (searchTerm.trim()) {
    const normalizedSearch =
      searchTerm.trim().toLowerCase();

    filteredQuestions =
      filteredQuestions.filter(
        (q) =>
          (q.questionText &&
            String(q.questionText)
              .toLowerCase()
              .includes(normalizedSearch)) ||
          (q.measureNumber &&
            String(q.measureNumber)
              .toLowerCase()
              .includes(normalizedSearch)) ||
          (q.tierCode &&
            String(q.tierCode)
              .toLowerCase()
              .includes(normalizedSearch))
      );
  }

  /*
   * Tier dropdown options.
   */
  const tierOptions = tiers
    .filter((tier: any) => tier.tierCode)
    .map((tier: any) => ({
      label: tier.tierCode,
      value: tier.tierCode,
      tierId: tier.tierId,
      providerType: tier.providerType,
    }));

  /*
   * Measure dropdown options.
   */
  const measureOptions = measures
    .filter(
      (measure: any) =>
        measure.measureNumber
    )
    .map((measure: any) => ({
      label: measure.measureNumber,
      value: measure.measureNumber,
      measureId: measure.measureId,
      providerType: measure.providerType,
    }));

  const createQuestionPayload = (
    question: any
  ) => {
    const normalizedPayload =
      normalizeFieldValues(
        dataManagementFormConfig.questions,
        question
      );

    /*
     * Preserve the Data Management normalization from Asset,
     * while also preserving US rich-text sanitization.
     */
    return {
      ...normalizedPayload,
      questionHtmlText: sanitize(
        normalizedPayload?.questionHtmlText ||
          ''
      ),
    };
  };

  const handleSaveQuestion = async () => {
    if (!selectedQuestion) {
      return;
    }

    const validationErrors =
      getFormValidationErrors(
        dataManagementFormConfig.questions,
        selectedQuestion
      );

    if (validationErrors.length > 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Fix the following',
        detail: validationErrors.join(', '),
        life: 4000,
      });

      return;
    }

    const payload =
      createQuestionPayload(
        selectedQuestion
      );

    try {
      setLoading(true);

      if (isAddMode) {
        await httpService.post(
          ApiEndpoints.AddQuestion,
          payload
        );
      } else {
        await httpService.put(
          ApiEndpoints.UpdateQuestion,
          payload
        );
      }

      const response =
        await fetchQuestions();

      setQuestions(
        Array.isArray(response?.data)
          ? response.data
          : []
      );

      setEditDialogVisible(false);
      setIsAddMode(false);
      setSelectedQuestion(null);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: isAddMode
          ? 'Question added successfully'
          : 'Question updated successfully',
        life: 3000,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Unknown error';

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          typeof msg === 'string'
            ? msg
            : 'Unable to save question.',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 24,
        position: 'relative',
        minHeight: 220,
      }}
      aria-busy={loading}
    >
      <DataManagementLoader
        visible={loading}
        label="Loading questions"
      />

      <Toast
        ref={toast}
        position="top-right"
        style={{ zIndex: 9999 }}
      />

      {error && (
        <div style={{ color: 'red' }}>
          {error}
        </div>
      )}

      {ImportPopup}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 12,
          }}
        >
          <Button
            label="Add New Question"
            icon="pi pi-plus"
            onClick={() => {
              setSelectedQuestion({
                questionId: 0,
                tierId: null,
                tierCode: '',
                measureId: null,
                measureNumber: '',
                isActive: true,
                questionText: '',
                number: null,
                dataFieldType: '',
                dataSource: '',
                sequence: null,
                conditionalDisplay: '',
                providerType: '',
                questionHtmlText: '',
              });

              setIsAddMode(true);
              setEditDialogVisible(true);
            }}
          />

          <Button
            label="Import"
            icon="pi pi-upload"
            className="p-button-secondary"
            onClick={showImport}
            style={{
              marginLeft: 8,
            }}
          />
        </div>

        <div className="question-search-anim">
          <input
            type="text"
            placeholder="Search by Question, Measure, or Tier..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

          <span className="pi pi-search" />
        </div>

        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={(e) =>
              setProviderTypeFilter(
                e.target.value
              )
            }
          >
            <option value="">
              All Provider Types
            </option>

            {providerTypeOptions.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              )
            )}
          </select>

          <span className="pi pi-angle-down" />
        </div>
      </div>

      <div
        style={{
          overflowX: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: '#fff',
            minWidth: 1200,
          }}
        >
          <thead>
            <tr
              style={{
                background: '#f0f4f8',
              }}
            >
              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 60,
                }}
              >
                #
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 100,
                }}
              >
                Tier Code
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 120,
                }}
              >
                Measure Number
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 80,
                }}
              >
                Number
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  minWidth: 200,
                }}
              >
                Question Text
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 100,
                }}
              >
                Data Field Type
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 100,
                }}
              >
                Data Source
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 80,
                }}
              >
                Sequence
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 100,
                }}
              >
                Provider Type
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 80,
                }}
              >
                Is Active
              </th>

              <th
                style={{
                  padding: 8,
                  border:
                    '1px solid #b0b6be',
                  width: 120,
                }}
              >
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredQuestions.length ===
            0 ? (
              <tr>
                <td
                  colSpan={11}
                  style={{
                    textAlign:
                      'center',
                    padding: 16,
                  }}
                >
                  No questions found.
                </td>
              </tr>
            ) : (
              filteredQuestions.map(
                (
                  question: any,
                  idx: number
                ) => (
                  <tr
                    key={
                      question.questionId ||
                      idx
                    }
                    style={{
                      background:
                        idx % 2 === 0
                          ? '#fff'
                          : '#f6f8fa',
                    }}
                  >
                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {idx + 1}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.tierCode ||
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.measureNumber ||
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.number ??
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                        maxWidth: 300,
                        overflow:
                          'hidden',
                        textOverflow:
                          'ellipsis',
                        whiteSpace:
                          'nowrap',
                      }}
                    >
                      <SafeHtml
                        html={
                          question.questionHtmlText ??
                          '-'
                        }
                      />
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.dataFieldType ||
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.dataSource ||
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.sequence ??
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.providerType ||
                        '-'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      {question.isActive
                        ? 'Yes'
                        : 'No'}
                    </td>

                    <td
                      style={{
                        padding: 8,
                        border:
                          '1px solid #eee',
                      }}
                    >
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-text"
                        style={{
                          marginRight: 8,
                        }}
                        onClick={() => {
                          setSelectedQuestion({
                            ...question,
                            questionHtmlText:
                              sanitize(
                                question.questionHtmlText ||
                                  ''
                              ),
                          });

                          setIsAddMode(
                            false
                          );

                          setEditDialogVisible(
                            true
                          );
                        }}
                      />

                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger"
                        onClick={() => {
                          setQuestionToDelete(
                            question
                          );

                          setDeleteDialogVisible(
                            true
                          );
                        }}
                      />
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 16,
          textAlign: 'right',
          color: '#888',
        }}
      >
        {/* Pagination placeholder */}
      </div>

      <Dialog
        header={
          isAddMode
            ? 'Add Question'
            : `Edit Question${
                selectedQuestion?.questionText
                  ? ` - ${selectedQuestion.questionText.substring(
                      0,
                      30
                    )}...`
                  : ''
              }`
        }
        visible={editDialogVisible}
        style={{
          width: '700px',
          maxWidth: '95vw',
        }}
        onHide={() => {
          setEditDialogVisible(false);
          setIsAddMode(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              onClick={() => {
                setEditDialogVisible(
                  false
                );

                setIsAddMode(false);
              }}
              className="p-button-text"
            />

            <Button
              label="Save"
              onClick={
                handleSaveQuestion
              }
              loading={loading}
              disabled={loading}
              autoFocus
            />
          </div>
        }
      >
        <QuestionEditForm
          editQuestion={
            selectedQuestion
          }
          setEditQuestion={
            setSelectedQuestion
          }
          providerTypes={
            providerTypes
          }
          tierOptions={tierOptions}
          measureOptions={
            measureOptions
          }
        />
      </Dialog>

      <Dialog
        header="Confirm Delete"
        visible={
          deleteDialogVisible
        }
        style={{
          width: '350px',
        }}
        onHide={() =>
          setDeleteDialogVisible(
            false
          )
        }
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent:
                'flex-end',
              gap: 8,
            }}
          >
            <Button
              label="Yes"
              autoFocus={false}
              onClick={() => {
                setQuestions(
                  questions.filter(
                    (question) =>
                      question.questionId !==
                      questionToDelete
                        ?.questionId
                  )
                );

                setDeleteDialogVisible(
                  false
                );

                setQuestionToDelete(
                  null
                );
              }}
              className="p-button-danger"
            />

            <Button
              label="No"
              autoFocus
              onClick={() => {
                setDeleteDialogVisible(
                  false
                );

                setQuestionToDelete(
                  null
                );
              }}
              className="p-button-secondary"
            />
          </div>
        }
      >
        <div
          style={{
            fontSize: 16,
            margin: '16px 0',
          }}
        >
          Are you sure you want to
          delete this question
          {questionToDelete?.questionText
            ? `: ${questionToDelete.questionText.substring(
                0,
                50
              )}...`
            : ''}
          ?
        </div>
      </Dialog>
    </div>
  );
};

interface QuestionEditFormProps {
  editQuestion: any;

  setEditQuestion: (
    question: any
  ) => void;

  providerTypes: {
    label: string;
    value: string;
  }[];

  tierOptions: {
    label: string;
    value: string;
    tierId: number;
    providerType: string;
  }[];

  measureOptions: {
    label: string;
    value: string;
    measureId: number;
    providerType: string;
  }[];
}

const QuestionEditForm: React.FC<
  QuestionEditFormProps
> = ({
  editQuestion,
  setEditQuestion,
  providerTypes,
  tierOptions,
  measureOptions,
}) => {
  const filteredTierOptions =
    editQuestion?.providerType
      ? tierOptions.filter(
          (tier) =>
            tier.providerType ===
            editQuestion.providerType
        )
      : tierOptions;

  const filteredMeasureOptions =
    editQuestion?.providerType
      ? measureOptions.filter(
          (measure) =>
            measure.providerType ===
            editQuestion.providerType
        )
      : measureOptions;

  return (
    <DataManagementForm
      fields={
        dataManagementFormConfig.questions
      }
      values={editQuestion}
      onChange={setEditQuestion}
      options={{
        providerType:
          providerTypes,

        tierCode:
          filteredTierOptions.map(
            (tier) => ({
              label: tier.label,
              value: tier.value,
              id: tier.tierId,
            })
          ),

        measureNumber:
          filteredMeasureOptions.map(
            (measure) => ({
              label:
                measure.label,
              value:
                measure.value,
              id:
                measure.measureId,
            })
          ),

        dataFieldType:
          DataFieldTypeOptions.map(
            (option) => ({
              label: option,
              value: option,
            })
          ),
      }}
    />
  );
};

export default QuestionsComponent;